import {
  CheckBoxType,
  IReportBuilderFormik,
  TSelectOptionsType,
} from 'domains/ReportBuilder/types';
import Checkbox from 'components/UI/Checkbox/checkbox';
import InfoBlock from 'components/UI/InfoBlock';
import { Field, FieldProps, FormikProps } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from '../../../styles.module.scss';

interface IStatistisProps {
  statisticsBy: IReportBuilderFormik['reportFilters']['statisticBy'];
  statistics: TSelectOptionsType[];
  setFieldValue: FormikProps<IReportBuilderFormik>['setFieldValue'];
}

const Statistics: FC<IStatistisProps> = ({
  statisticsBy,
  statistics,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isPartiallyChecked, setIsPartiallyChecked] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    const updatedStatisticBy = statistics.reduce(
      (acc, stat) => ({ ...acc, [stat.value]: checked }),
      {},
    );
    setIsAllChecked(!!checked);
    setFieldValue('reportFilters.statisticBy', updatedStatisticBy);
  };

  useEffect(() => {
    const allStatisticsBy = statistics.reduce(
      (acc, stat) => ({
        ...acc,
        [stat.value]: statisticsBy[stat.value] ?? false,
      }),
      {} as CheckBoxType,
    );

    const allStatisticKeys = Object.keys(allStatisticsBy);

    if (allStatisticKeys.length === 0) {
      setIsAllChecked(false);
      setIsPartiallyChecked(false);
      return;
    }

    const allChecked = allStatisticKeys.every(
      (key) => allStatisticsBy[key] === true,
    );
    const someChecked = allStatisticKeys.some(
      (key) => allStatisticsBy[key] === true,
    );
    const someUnchecked = allStatisticKeys.some(
      (key) => allStatisticsBy[key] === false,
    );

    setIsAllChecked(allChecked);
    setIsPartiallyChecked(someChecked && someUnchecked);
  }, [statisticsBy, statistics]);

  return (
    <>
      <InfoBlock text={t(`reportbuilder.canChooseData`)} />
      <Checkbox
        label={t(
          `campaigns_page.campaign_settings.additional.statistics.select_all`,
        )}
        isPartiallyChecked={isPartiallyChecked}
        inputAttributes={{
          type: 'checkbox',
          checked: isAllChecked,
          onChange: (e) => handleSelectAll(e.target.checked),
        }}
        className={css.statistics}
      />

      {statistics.map((statistic) => (
        <Field
          key={statistic.value}
          name={`reportFilters.statisticBy.${statistic.value}`}
        >
          {({ form, field }: FieldProps) => (
            <Checkbox
              label={statistic.label}
              inputAttributes={{
                type: 'checkbox',
                checked: Boolean(field.value),
                onChange: (e) =>
                  form.setFieldValue(field.name, e.target.checked),
              }}
              className={css.statistics}
            />
          )}
        </Field>
      ))}
    </>
  );
};

export default Statistics;
